// src/pages/LiveMode.jsx
import React, { useState, useEffect, useRef } from 'react';
import { Search, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import BackingTrackPicker from '../components/BackingTrackPicker';
import BackingTrackPlayer from '../components/BackingTrackPlayer';

import {
  db,
  listenToLiveGig,
  extendGigTime,
  endLiveGig,
  acceptSongRequest,
  rejectSongRequest,
  updateJukeboxSettings,
  getSortedSongRequests,
} from '../firebase-utils';

import {
  doc,
  getDoc,
  updateDoc,
  arrayUnion,
  collection,
  getDocs,
  query,
  onSnapshot,
} from 'firebase/firestore';

// ─── tab styles ──────────────────────────────────────────────
const TAB = {
  queue: {
    on:  { background: 'linear-gradient(135deg,#0ea5e9,#0284c7)', color: '#fff', border: '2px solid #38bdf8', boxShadow: '0 0 18px rgba(14,165,233,0.6)', fontWeight: 900 },
    off: { background: 'rgba(14,165,233,0.08)', color: '#64748b', border: '2px solid rgba(14,165,233,0.15)', fontWeight: 700 },
  },
  requests: {
    on:  { background: 'linear-gradient(135deg,#db2777,#be185d)', color: '#fff', border: '2px solid #f472b6', boxShadow: '0 0 18px rgba(219,39,119,0.6)', fontWeight: 900 },
    off: { background: 'rgba(219,39,119,0.08)', color: '#64748b', border: '2px solid rgba(219,39,119,0.15)', fontWeight: 700 },
  },
  master: {
    on:  { background: 'linear-gradient(135deg,#16a34a,#15803d)', color: '#fff', border: '2px solid #4ade80', boxShadow: '0 0 18px rgba(22,163,74,0.6)', fontWeight: 900 },
    off: { background: 'rgba(22,163,74,0.08)', color: '#64748b', border: '2px solid rgba(22,163,74,0.15)', fontWeight: 700 },
  },
};

// ─── Match a queued song against the song bank ───────────────
const findBankTrack = (song, songBank) => {
  if (!song || !songBank || songBank.length === 0) return null;
  const normalize = (s) => (s || '').toLowerCase().trim().replace(/[^a-z0-9]/g, '');
  const titleA = normalize(song.title);
  const artistA = normalize(song.artist);
  return songBank.find(entry => {
    const titleB = normalize(entry.title);
    const artistB = normalize(entry.artist);
    const titleMatch = titleA === titleB || titleA.includes(titleB) || titleB.includes(titleA);
    const artistMatch = artistA === artistB || artistA.includes(artistB) || artistB.includes(artistA);
    return titleMatch && artistMatch && entry.youtubeId;
  }) || null;
};

export default function LiveMode({ liveGig, onModeChange, setGigs, gigs }) {
  const { currentUser } = useAuth();

  const [liveGigData, setLiveGigData] = useState({
    votes: {}, comments: [], donations: [], queuedSongs: [], masterPlaylist: [],
    currentSong: null, playedSongs: [], scheduledEndTime: null, scheduledEndTimeMs: null,
    songRequests: [], requestsEnabled: true, jukeboxMode: false, jukeboxPrice: 0,
    maxQueueSize: 20, lastVoteTime: {},
    audienceTracking: { totalJoins: 0, currentlyActive: 0, joinedUsers: {} },
  });

  const [songBank, setSongBank] = useState([]);
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [showTimeWarning, setShowTimeWarning] = useState(false);
  const [isEndingGig, setIsEndingGig] = useState(false);
  const [liveTab, setLiveTab] = useState('queue');
  const [liveMasterSearch, setLiveMasterSearch] = useState('');
  const [backingTrackPickerSong, setBackingTrackPickerSong] = useState(null);
  const [activePlayers, setActivePlayers] = useState(new Set());

  useEffect(() => {
    const q = query(collection(db, 'backingTracks'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const entries = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setSongBank(entries);
    }, (err) => { console.warn('Song bank listener error:', err); });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!liveGig?.id) return;
    const unsubscribe = listenToLiveGig(liveGig.id, (gigData) => {
      setLiveGigData({
        votes: gigData.votes || {},
        comments: gigData.comments || [],
        donations: gigData.donations || [],
        queuedSongs: gigData.queuedSongs || [],
        masterPlaylist: gigData.masterPlaylist || [],
        currentSong: gigData.currentSong || null,
        playedSongs: gigData.playedSongs || [],
        scheduledEndTime: gigData.scheduledEndTime || null,
        scheduledEndTimeMs: gigData.scheduledEndTimeMs || null,
        songRequests: gigData.songRequests || [],
        requestsEnabled: gigData.requestsEnabled !== false,
        jukeboxMode: gigData.jukeboxMode || false,
        jukeboxPrice: gigData.jukeboxPrice || 0,
        maxQueueSize: gigData.songLimit || gigData.maxQueueSize || 20,
        lastVoteTime: gigData.lastVoteTime || {},
        audienceTracking: gigData.audienceTracking || { totalJoins: 0, currentlyActive: 0, joinedUsers: {} },
      });
    });
    return () => unsubscribe();
  }, [liveGig?.id, currentUser]);

  useEffect(() => {
    if (!liveGig?.id || isEndingGig) return;
    const checkTimer = async () => {
      if (liveGigData.scheduledEndTimeMs) {
        const remainingMs = liveGigData.scheduledEndTimeMs - Date.now();
        const remainingMinutes = Math.floor(remainingMs / 60000);
        setTimeRemaining(remainingMs);
        if (remainingMinutes <= 10 && remainingMinutes > 0 && !showTimeWarning) {
          setShowTimeWarning(true);
          alert(`⚠️ Your gig will auto-end in ${remainingMinutes} minutes!\n\nClick "Add 1 Hour" to extend.`);
        }
        if (remainingMinutes <= 0 && !isEndingGig) await handleEndGig('⏰ Time limit reached!', true);
        return;
      }
      if (!liveGigData.scheduledEndTime) return;
      const st = liveGigData.scheduledEndTime;
      const endTime = (() => {
        if (st instanceof Date) return st;
        if (st?.toDate) return st.toDate();
        if (st?.seconds) return new Date(st.seconds * 1000);
        if (typeof st === 'number') return new Date(st);
        if (typeof st === 'string') return new Date(st);
        return null;
      })();
      if (!endTime) return;
      const remaining = endTime - new Date();
      const remainingMinutes = Math.floor(remaining / 60000);
      setTimeRemaining(remaining);
      if (remainingMinutes <= 10 && remainingMinutes > 0 && !showTimeWarning) {
        setShowTimeWarning(true);
        alert(`⚠️ Your gig will auto-end in ${remainingMinutes} minutes!`);
      }
      if (remainingMinutes <= 0 && !isEndingGig) await handleEndGig('⏰ Time limit reached!', true);
    };
    checkTimer();
    const interval = setInterval(checkTimer, 60000);
    return () => clearInterval(interval);
  }, [liveGig?.id, liveGigData.scheduledEndTimeMs, liveGigData.scheduledEndTime, showTimeWarning, isEndingGig]);

  const handleEndGig = async (customMessage = null, skipConfirm = false) => {
    if (isEndingGig) return;
    if (!skipConfirm && !window.confirm('End this gig?')) return;
    setIsEndingGig(true);
    try {
      await endLiveGig(liveGig.id);
      const updatedGigs = (gigs || []).map(g =>
        g.id === liveGig.gigId || (g.venueName === liveGig.venueName && g.date === liveGig.date)
          ? { ...g, status: 'ended' } : g
      );
      if (setGigs) setGigs(updatedGigs);
      if (currentUser) {
        localStorage.setItem(`GigWave_gigs_${currentUser.uid}`, JSON.stringify(updatedGigs));
        localStorage.removeItem(`GigWave_live_${currentUser.uid}`);
      }
      onModeChange('artist');
      alert(customMessage || '✅ Gig ended!');
    } catch (e) { alert('Error: ' + e.message); }
    finally { setIsEndingGig(false); }
  };

  const sortArtistQueue = (songs) => {
    const accepted = [], voted = [], unvoted = [];
    songs.forEach(song => {
      const isAccepted = liveGigData.songRequests?.some(r => r.songId === song.id && r.status === 'accepted');
      const v = liveGigData.votes[Math.floor(song.id)] || 0;
      if (isAccepted) accepted.push(song);
      else if (v > 0) voted.push(song);
      else unvoted.push(song);
    });
    const byVotes = (a, b) => {
      const va = liveGigData.votes[Math.floor(a.id)] || 0;
      const vb = liveGigData.votes[Math.floor(b.id)] || 0;
      if (vb !== va) return vb - va;
      const ta = liveGigData.lastVoteTime?.[Math.floor(a.id)];
      const tb = liveGigData.lastVoteTime?.[Math.floor(b.id)];
      if (!ta && !tb) return 0;
      if (!ta) return 1;
      if (!tb) return -1;
      return new Date(ta) - new Date(tb);
    };
    accepted.sort(byVotes); voted.sort(byVotes);
    return [...accepted, ...voted, ...unvoted];
  };

  const markSongPlayed = async (songId) => {
    try {
      const gigRef = doc(db, 'liveGigs', String(liveGig.id));
      await updateDoc(gigRef, { playedSongs: arrayUnion(songId) });
      setActivePlayers(prev => { const n = new Set(prev); n.delete(songId); return n; });
    } catch (e) { alert('Error marking song as played: ' + e.message); }
  };

  const togglePlayer = (songId) => {
    setActivePlayers(prev => {
      const n = new Set(prev);
      n.has(songId) ? n.delete(songId) : n.add(songId);
      return n;
    });
  };

  if (!liveGig) {
    return (
      <div className="rock-background min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-white text-xl mb-4">No active gig found.</p>
          <button onClick={() => onModeChange('artist')} className="btn btn-electric">← Back to Dashboard</button>
        </div>
      </div>
    );
  }

  const pendingCount = (liveGigData.songRequests || []).filter(r => r.status === 'pending').length;
  const readyQueue = sortArtistQueue(liveGigData.queuedSongs || []).filter(s => !liveGigData.playedSongs?.includes(s.id));

  return (
    <div className="rock-background min-h-screen pb-24 md:pb-4">
      <div className="max-w-4xl mx-auto px-4 pt-4">

        {/* ══ LIVE HEADER ══ */}
        <div className="artist-section-card mb-3 p-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-bold tracking-wider" style={{ color: '#39FF14' }}>
              <span className="inline-block animate-pulse" style={{ filter: 'drop-shadow(0 0 8px #39FF14)' }}>🟢</span> LIVE NOW
            </span>
            <div className="text-right">
              <p className="text-gray-light text-[9px] mb-0.5">⏰ Time Left</p>
              <p className="text-white font-bold text-sm leading-none">
                {timeRemaining != null
                  ? `${Math.floor(timeRemaining / 3600000)}h ${Math.floor((timeRemaining % 3600000) / 60000)}m`
                  : '—'}
              </p>
            </div>
          </div>
          <h2 className="concert-heading text-xl text-electric text-center mb-3">{liveGig.artistName}</h2>
          {showTimeWarning && (
            <div className="bg-orange-500/20 border border-orange-400 rounded-lg p-2 mb-2">
              <p className="text-orange-300 font-bold text-center text-xs">
                ⚠️ Only {Math.floor((timeRemaining || 0) / 60000)} minutes left!
              </p>
            </div>
          )}
          <div className="flex gap-2">
            <button
              onClick={async () => {
                try { await extendGigTime(liveGig.id, 60); setShowTimeWarning(false); alert('✅ Added 1 hour!'); }
                catch (e) { alert('Error: ' + e.message); }
              }}
              className="flex-1 font-bold py-2 px-2 rounded-lg transition-all text-xs"
              style={{ background: 'linear-gradient(135deg,#00d9ff,#00ffc8)', color: '#000', border: '2px solid rgba(0,217,255,0.8)', boxShadow: '0 0 15px rgba(0,217,255,0.6)' }}
            >⏱️ Add 1 Hour</button>
            <button
              onClick={handleEndGig}
              className="flex-1 font-bold py-2 px-2 rounded-lg transition-all text-xs"
              style={{ background: 'linear-gradient(135deg,#ff006b,#ff4444)', color: '#fff', border: '2px solid rgba(255,0,107,0.8)', boxShadow: '0 0 15px rgba(255,0,107,0.6)' }}
            >🛑 End Gig</button>
          </div>
        </div>

        {/* ══ STATS ROW ══ */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 mb-3">
          {[
            { icon: '🟢', val: liveGigData.audienceTracking?.currentlyActive || 0, label: 'Live Now', c: '#39FF14', bc: 'rgba(57,255,20,0.2)' },
            { icon: '👥', val: liveGigData.audienceTracking?.totalJoins || 0, label: 'Total Joins', c: '#00d9ff', bc: 'rgba(0,217,255,0.2)' },
            { icon: '⚡', val: Object.values(liveGigData.votes || {}).reduce((s, v) => s + v, 0), label: 'Total Votes', c: '#ff006b', bc: 'rgba(255,0,107,0.2)' },
            { icon: '📋', val: pendingCount, label: 'Requests', c: '#ff7b00', bc: 'rgba(255,123,0,0.2)' },
          ].map(({ icon, val, label, c, bc }) => (
            <div key={label} className="rounded-lg px-3 py-2 flex items-center gap-2"
              style={{ background: 'rgba(0,0,0,0.4)', border: `1px solid ${bc}`, backdropFilter: 'blur(8px)' }}>
              <span className="text-lg">{icon}</span>
              <div>
                <div className="font-bold text-base leading-none" style={{ color: c }}>{val}</div>
                <div className="text-gray-light text-[9px] leading-tight">{label}</div>
              </div>
            </div>
          ))}
        </div>

        {/* ══ TAB NAVIGATION ══ */}
        <div className="mb-3 p-1.5 rounded-xl"
          style={{ background: 'rgba(0,0,0,0.6)', border: '1px solid rgba(255,255,255,0.07)', backdropFilter: 'blur(12px)' }}>
          <div className="flex gap-1.5">
            <button onClick={() => setLiveTab('queue')}
              className="flex-1 py-2.5 px-2 rounded-lg text-[10px] tracking-wide uppercase transition-all duration-200"
              style={liveTab === 'queue' ? TAB.queue.on : TAB.queue.off}>
              🎵 Song Queue <span className="opacity-75">({readyQueue.length})</span>
            </button>
            <button onClick={() => setLiveTab('requests')}
              className="flex-1 py-2.5 px-2 rounded-lg text-[10px] tracking-wide uppercase transition-all duration-200 relative"
              style={liveTab === 'requests' ? TAB.requests.on : TAB.requests.off}>
              🎤 Song Requests
              {pendingCount > 0 && (
                <span className="absolute -top-1.5 -right-1 text-white text-[9px] font-black rounded-full w-4 h-4 flex items-center justify-center"
                  style={{ background: '#ff006b', boxShadow: '0 0 8px rgba(255,0,107,0.9)' }}>
                  {pendingCount}
                </span>
              )}
            </button>
            <button onClick={() => setLiveTab('master')}
              className="flex-1 py-2.5 px-2 rounded-lg text-[10px] tracking-wide uppercase transition-all duration-200"
              style={liveTab === 'master' ? TAB.master.on : TAB.master.off}>
              📚 Master Playlist <span className="opacity-75">({liveGigData.masterPlaylist?.length || 0})</span>
            </button>
          </div>
        </div>

        {/* ══ TAB CONTENT ══ */}
        <div className="artist-section-card p-3">

          {/* ──────────── SONG QUEUE ──────────── */}
          {liveTab === 'queue' && (
            <div>
              <h3 className="artist-section-header mb-3 text-base">
                <span className="emoji-icon">🎵</span><span>SONG QUEUE</span>
              </h3>
              <div className="flex items-center gap-2 mb-3 px-1">
                <span className="text-[10px]" style={{ color: songBank.length > 0 ? '#39FF14' : '#888' }}>
                  {songBank.length > 0
                    ? `🎵 ${songBank.filter(e => e.youtubeId).length} tracks ready in Song Bank`
                    : '⏳ Loading song bank...'}
                </span>
              </div>
              <div className="mb-4">
                <div className="flex items-center gap-2 mb-2 px-1">
                  <span className="text-neon text-xs font-bold">▶️ Ready to Play</span>
                  <span className="text-black text-[10px] font-bold px-2 py-0.5 rounded-full"
                    style={{ background: '#39FF14' }}>{readyQueue.length}</span>
                </div>
                {readyQueue.length === 0 ? (
                  <div className="bg-white/5 border border-white/10 rounded-lg p-6 text-center">
                    <p className="text-gray-light text-sm">No songs in queue. Add from Master Playlist tab! 🎸</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {readyQueue.map((song, index) => {
                      const title5 = song.title.split(' ').slice(0, 5).join(' ') + (song.title.split(' ').length > 5 ? '...' : '');
                      const votes = liveGigData.votes?.[Math.floor(song.id)] || 0;
                      const bankEntry = findBankTrack(song, songBank);
                      const hasTrack = !!bankEntry;
                      const songWithTrack = hasTrack
                        ? { ...song, backingTrackUrl: `https://www.youtube.com/watch?v=${bankEntry.youtubeId}`, backingTrackSource: 'youtube', backingTrackName: bankEntry.title }
                        : song;
                      const isPlaying = activePlayers.has(song.id);
                      return (
                        <div key={song.id} className="rounded-xl p-3"
                          style={{
                            background: hasTrack
                              ? 'linear-gradient(135deg,rgba(124,58,237,0.12),rgba(99,102,241,0.06))'
                              : 'linear-gradient(135deg,rgba(0,255,100,0.07),rgba(0,200,80,0.03))',
                            border: hasTrack
                              ? '1px solid rgba(124,58,237,0.4)'
                              : '1px solid rgba(0,255,100,0.25)',
                            boxShadow: '0 2px 12px rgba(0,0,0,0.3)'
                          }}>
                          <div className="flex justify-between items-start gap-2 mb-1">
                            <div className="flex items-start gap-2 flex-1 min-w-0">
                              <span className="font-black text-sm flex-shrink-0" style={{ color: '#39FF14' }}>{index + 1}</span>
                              <div className="text-white font-semibold text-sm leading-tight truncate">{title5}</div>
                            </div>
                            <div className="flex items-center gap-2">
                              {hasTrack && (
                                <span className="text-[9px] px-1.5 py-0.5 rounded font-bold"
                                  style={{ background: 'rgba(124,58,237,0.3)', color: '#c4b5fd', border: '1px solid rgba(124,58,237,0.4)' }}>
                                  🎵 BANK
                                </span>
                              )}
                              <div className="font-bold text-xs" style={{ color: '#ff006b' }}>⚡ {votes}</div>
                            </div>
                          </div>
                          <div className="text-xs pl-6 mb-3" style={{ color: 'rgba(180,255,200,0.7)' }}>{song.artist}</div>
                          <div className="pl-6 flex items-center gap-2 flex-wrap">
                            {hasTrack ? (
                              <button
                                onClick={() => togglePlayer(song.id)}
                                className="text-xs px-3 py-1.5 rounded-lg font-bold transition-all duration-150"
                                style={isPlaying
                                  ? { background: 'linear-gradient(135deg,#00ff88,#00d9a0)', color: '#000', border: '2px solid #00ff88', boxShadow: '0 0 14px rgba(0,255,136,0.7)' }
                                  : { background: 'linear-gradient(135deg,#7c3aed,#a855f7)', color: '#fff', border: '2px solid #a855f7', boxShadow: '0 0 14px rgba(168,85,247,0.6)' }
                                }
                              >
                                {isPlaying ? '⏸ Hide Player' : '▶ Play Backing Track'}
                              </button>
                            ) : (
                              <button
                                onClick={() => setBackingTrackPickerSong(song)}
                                className="text-xs px-3 py-1.5 rounded-lg font-bold transition-all duration-150"
                                style={{ background: 'linear-gradient(135deg,#f59e0b,#f97316)', color: '#000', border: '2px solid #f59e0b', boxShadow: '0 0 10px rgba(245,158,11,0.5)' }}
                              >
                                ＋ Add Backing Track
                              </button>
                            )}
                            {!isPlaying && (
                              <button
                                onClick={async () => {
                                  if (window.confirm(`Mark "${song.title}" as played?`)) {
                                    try {
                                      const gigRef = doc(db, 'liveGigs', String(liveGig.id));
                                      await updateDoc(gigRef, { playedSongs: arrayUnion(song.id) });
                                      alert('✅ Song marked as played!');
                                    } catch (e) { alert('Error: ' + e.message); }
                                  }
                                }}
                                className="text-xs px-3 py-1.5 rounded-lg font-bold transition-all ml-auto"
                                style={{ background: 'linear-gradient(135deg,#06b6d4,#0891b2)', color: '#fff', border: '2px solid #06b6d4', boxShadow: '0 0 10px rgba(6,182,212,0.4)' }}
                              >✓ Mark Played</button>
                            )}
                          </div>
                          {hasTrack && isPlaying && (
                            <div className="mt-3 pl-2">
                              <BackingTrackPlayer
                                song={songWithTrack}
                                onMarkPlayed={() => markSongPlayed(song.id)}
                                onRemoveTrack={() => togglePlayer(song.id)}
                                onToggle={(enabled) => { if (!enabled) togglePlayer(song.id); }}
                              />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              {(liveGigData.playedSongs || []).length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-2 px-1">
                    <span className="text-gray-400 text-xs font-bold">✅ Already Played</span>
                    <span className="bg-gray-600 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                      {liveGigData.playedSongs.length}
                    </span>
                  </div>
                  <div className="space-y-1.5">
                    {sortArtistQueue(liveGigData.queuedSongs || [])
                      .filter(s => liveGigData.playedSongs.includes(s.id))
                      .map(song => {
                        const title5 = song.title.split(' ').slice(0, 5).join(' ') + (song.title.split(' ').length > 5 ? '...' : '');
                        const votes = liveGigData.votes?.[Math.floor(song.id)] || 0;
                        return (
                          <div key={song.id} className="rounded-lg p-2 opacity-50"
                            style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
                            <div className="flex justify-between items-center gap-2">
                              <div className="flex items-center gap-2 flex-1 min-w-0">
                                <span className="text-gray-400 font-bold text-xs">✓</span>
                                <div className="text-white text-sm truncate line-through">{title5}</div>
                                <div className="text-gray-400 text-xs">— {song.artist}</div>
                              </div>
                              <div className="text-gray-500 font-bold text-xs">⚡ {votes}</div>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ──────────── SONG REQUESTS ──────────── */}
          {liveTab === 'requests' && (
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="artist-section-header text-base mb-0">
                  <span className="emoji-icon">🎤</span><span>SONG REQUESTS</span>
                </h3>
                <button
                  onClick={async () => {
                    const newStatus = !(liveGigData?.requestsEnabled !== false);
                    try {
                      await updateJukeboxSettings(liveGig.id, { requestsEnabled: newStatus, jukeboxMode: false, jukeboxPrice: 0 });
                      setLiveGigData(prev => ({ ...prev, requestsEnabled: newStatus }));
                      alert(newStatus ? '✅ Requests ENABLED' : '❌ Requests DISABLED');
                    } catch (e) { alert('Error: ' + e.message); }
                  }}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-bold text-xs transition-all"
                  style={liveGigData.requestsEnabled !== false
                    ? { background: 'rgba(57,255,20,0.15)', color: '#39FF14', border: '1.5px solid rgba(57,255,20,0.4)', boxShadow: '0 0 8px rgba(57,255,20,0.2)' }
                    : { background: 'rgba(255,255,255,0.05)', color: '#666', border: '1.5px solid rgba(255,255,255,0.1)' }
                  }
                >
                  {liveGigData.requestsEnabled !== false ? '🟢 On' : '⚫ Off'}
                </button>
              </div>
              <p className="text-center text-white/60 text-xs mb-3">
                {(liveGigData.songRequests || []).length} total request{(liveGigData.songRequests || []).length !== 1 ? 's' : ''}
              </p>
              {(liveGigData.songRequests || []).length === 0 ? (
                <div className="bg-white/5 border border-white/10 rounded-lg p-6 text-center">
                  <p className="text-4xl mb-2">📭</p>
                  <p className="text-gray-light text-sm">No song requests yet</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {getSortedSongRequests(liveGigData.songRequests).map((req, i) => {
                    const title5 = req.songTitle.split(' ').slice(0, 5).join(' ') + (req.songTitle.split(' ').length > 5 ? '...' : '');
                    const isPending = req.status === 'pending';
                    const isAccepted = req.status === 'accepted';
                    return (
                      <div key={req.id} className="rounded-lg p-2.5 border"
                        style={{
                          background: isPending ? 'rgba(147,51,234,0.1)' : isAccepted ? 'rgba(34,197,94,0.1)' : 'rgba(100,100,100,0.08)',
                          borderColor: isPending ? 'rgba(147,51,234,0.35)' : isAccepted ? 'rgba(34,197,94,0.35)' : 'rgba(100,100,100,0.25)',
                        }}>
                        <div className="flex items-start gap-2 mb-1">
                          <span className="font-bold text-sm flex-shrink-0"
                            style={{ color: isPending ? '#c084fc' : isAccepted ? '#86efac' : '#6b7280' }}>{i + 1}</span>
                          <div className="text-white font-semibold text-sm flex-1 min-w-0 truncate">{title5}</div>
                        </div>
                        <div className="text-xs mb-1 pl-6" style={{ color: isPending ? '#e9d5ff' : isAccepted ? '#bbf7d0' : '#9ca3af' }}>{req.songArtist}</div>
                        <div className="text-xs mb-2 pl-6" style={{ color: isPending ? 'rgba(216,180,254,0.7)' : 'rgba(156,163,175,0.7)' }}>👤 {req.requesterName}</div>
                        {req.message?.trim() && (
                          <div className="rounded-lg px-3 py-2 mb-2 ml-6"
                            style={{ background: 'rgba(219,39,119,0.1)', border: '1px solid rgba(219,39,119,0.35)' }}>
                            <p className="text-pink-300 text-[10px] font-bold uppercase tracking-wider mb-1">💌 Personal Message</p>
                            <p className="text-white text-xs italic leading-relaxed">"{req.message}"</p>
                          </div>
                        )}
                        <div className="flex gap-2 pl-6">
                          {isPending && (
                            <>
                              <button onClick={async () => { try { await acceptSongRequest(liveGig.id, req.id); } catch (e) { alert(e.message); } }}
                                className="font-bold text-xs px-3 py-1.5 rounded-lg transition-all"
                                style={{ background: 'linear-gradient(135deg,#22c55e,#16a34a)', color: '#fff', border: '2px solid #22c55e', boxShadow: '0 0 8px rgba(34,197,94,0.4)' }}
                              >✓ Accept</button>
                              <button onClick={async () => { try { await rejectSongRequest(liveGig.id, req.id); } catch (e) { alert(e.message); } }}
                                className="font-bold text-xs px-3 py-1.5 rounded-lg transition-all"
                                style={{ background: 'linear-gradient(135deg,#ef4444,#dc2626)', color: '#fff', border: '2px solid #ef4444', boxShadow: '0 0 8px rgba(239,68,68,0.4)' }}
                              >✗ Reject</button>
                            </>
                          )}
                          {isAccepted && <span className="text-green-400 font-bold text-xs">✓ Accepted</span>}
                          {req.status === 'rejected' && <span className="text-red-400 font-bold text-xs">✗ Rejected</span>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* ──────────── MASTER PLAYLIST ──────────── */}
          {liveTab === 'master' && (
            <div>
              <h3 className="artist-section-header mb-3 text-base">
                <span className="emoji-icon">📚</span><span>MASTER PLAYLIST</span>
              </h3>
              <div className="mb-3 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                <input type="text" placeholder="Search songs or artists..."
                  value={liveMasterSearch} onChange={e => setLiveMasterSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-black/50 border border-neon/30 rounded-lg text-white placeholder-gray-500 focus:border-neon focus:outline-none text-sm" />
                {liveMasterSearch && (
                  <button onClick={() => setLiveMasterSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white">
                    <X size={16} />
                  </button>
                )}
              </div>
              <p className="text-center text-white/60 text-xs mb-3">{liveGigData.masterPlaylist?.length || 0} total songs</p>
              {(!liveGigData.masterPlaylist || liveGigData.masterPlaylist.length === 0) ? (
                <div className="bg-white/5 border border-white/10 rounded-lg p-6 text-center">
                  <p className="text-gray-light text-sm">No songs in master playlist. 🎵</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {liveGigData.masterPlaylist
                    .filter(song => {
                      if (!liveMasterSearch) return true;
                      const q = liveMasterSearch.toLowerCase();
                      return song.title.toLowerCase().includes(q) || song.artist.toLowerCase().includes(q);
                    })
                    .map(song => {
                      const isInQueue = (liveGigData.queuedSongs || []).some(q => q.id === song.id);
                      const inBank = !!findBankTrack(song, songBank);
                      return (
                        <div key={song.id} className="rounded-lg p-3"
                          style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.09)' }}>
                          <div className="flex items-center gap-3">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-0.5">
                                <div className="text-white font-bold text-sm truncate">{song.title}</div>
                                {inBank && (
                                  <span className="text-[9px] px-1 py-0.5 rounded flex-shrink-0"
                                    style={{ background: 'rgba(124,58,237,0.25)', color: '#c4b5fd' }}>🎵</span>
                                )}
                              </div>
                              <div className="text-gray-300 text-xs">{song.artist}</div>
                            </div>
                            {isInQueue ? (
                              <button disabled className="font-bold text-xs px-3 py-2 rounded-lg cursor-not-allowed"
                                style={{ background: 'rgba(34,197,94,0.12)', color: '#4ade80', border: '1.5px solid rgba(34,197,94,0.3)' }}
                              >✓ Added</button>
                            ) : (
                              <button
                                onClick={async () => {
                                  try {
                                    const gigRef = doc(db, 'liveGigs', String(liveGig.id));
                                    const snap = await getDoc(gigRef);
                                    const currentQueue = snap.data().queuedSongs || [];
                                    const votes = snap.data().votes || {};
                                    const updated = [...currentQueue, song].sort((a, b) =>
                                      (votes[Math.floor(b.id)] || 0) - (votes[Math.floor(a.id)] || 0)
                                    );
                                    await updateDoc(gigRef, { queuedSongs: updated });
                                  } catch (e) { alert('Error: ' + e.message); }
                                }}
                                className="font-bold text-xs px-3 py-2 rounded-lg transition-all"
                                style={{ background: 'linear-gradient(135deg,#16a34a,#15803d)', color: '#fff', border: '2px solid #4ade80', boxShadow: '0 0 8px rgba(22,163,74,0.4)' }}
                              >+ Add to Queue</button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                </div>
              )}
            </div>
          )}

        </div>
      </div>

      {/* ══ BACKING TRACK PICKER MODAL ══ */}
      {backingTrackPickerSong && (
        <BackingTrackPicker
          song={backingTrackPickerSong}
          onSave={() => setBackingTrackPickerSong(null)}
          onClose={() => setBackingTrackPickerSong(null)}
        />
      )}
    </div>
  );
}
